/**

 * Admin KYC — single overview endpoint with legacy fallback.

 */



import apiClient from './apiClient';

import { parseKycListResponse } from './adminKycLegacyParse';



export type KycOverviewStatus = 'ALL' | 'PENDING' | 'APPROVED' | 'REJECTED';



const KYC_REQUEST_TIMEOUT_MS = 120000;

const OVERVIEW_CACHE_MS = 60_000;



export interface KycCatalogService {

  id: string;

  service_name: string;

}



export interface KycOverviewProvider {

  service_provider_id: string;

  user_id: string;

  service_provider_name: string | null;

  service_provider_email: string | null;

  service_provider_phone: string | null;

  provider_role: string | null;

  profile_image?: string | null;

  dob?: string | null;

  date_joined?: string | null;

  active_service_city?: string | null;

  service_area_cities?: string[] | null;

  captain_work_enabled?: boolean | null;

  work_status_label?: string | null;

  verification_status: string;

  total_pending: number;

  total_items: number;

  documents: Record<string, unknown>[];

  skills: Record<string, unknown>[];

}



export interface KycOverviewPayload {

  documents: Record<string, unknown>[];

  skills: Record<string, unknown>[];

  providers: KycOverviewProvider[];

  services: KycCatalogService[];

  meta?: {

    status_filter?: string;

    document_count?: number;

    skill_count?: number;

    provider_count?: number;

    source?: 'overview' | 'legacy';

    load_warnings?: string[];

  };

}



let overviewInflight: Promise<KycOverviewPayload> | null = null;

let overviewCache: { at: number; payload: KycOverviewPayload } | null = null;



function parseOverviewBody(body: unknown): KycOverviewPayload | null {

  if (!body || typeof body !== 'object') return null;

  const envelope = body as { success?: boolean; statusCode?: number; data?: unknown };

  if (envelope.success === false || (typeof envelope.statusCode === 'number' && envelope.statusCode >= 400)) {

    return null;

  }



  let data = envelope.data ?? body;

  if (Array.isArray(data)) data = data[0];

  if (!data || typeof data !== 'object') return null;

  const o = data as KycOverviewPayload;



  const documents = Array.isArray(o.documents) ? o.documents : [];

  const skills = Array.isArray(o.skills) ? o.skills : [];

  const providers = Array.isArray(o.providers) ? o.providers : [];

  const services = Array.isArray(o.services) ? o.services : [];



  return { documents, skills, providers, services, meta: o.meta };

}



function isPendingStatus(status: string | null | undefined): boolean {

  return String(status ?? '').trim().toUpperCase() === 'PENDING';

}



function computeVerificationStatus(

  documents: { document_status?: string }[],

  skills: { skill_status?: string }[],

): string {

  const statuses = [

    ...documents.map((d) => String(d.document_status ?? '').trim().toUpperCase()),

    ...skills.map((s) => String(s.skill_status ?? '').trim().toUpperCase()),

  ].filter(Boolean);

  if (statuses.length === 0) return 'PENDING';

  if (statuses.some((s) => s === 'PENDING')) return 'PENDING';

  if (statuses.every((s) => s === 'APPROVED')) return 'VERIFIED';

  if (statuses.every((s) => s === 'REJECTED')) return 'REJECTED';

  if (statuses.some((s) => s === 'REJECTED')) return 'PARTIAL';

  return 'PENDING';

}



function providerLabelFromRow(

  pid: string,

  row: Record<string, unknown>,

): string {

  const name = String(row.service_provider_name ?? '').trim();

  if (name) return name;

  const email = String(row.service_provider_email ?? '').trim();

  if (email) return email;

  const phone = String(row.service_provider_phone ?? '').trim();

  if (phone) return phone;

  return pid ? `Provider ${pid.slice(0, 8)}` : 'Unknown Provider';

}



/** Client-side status filter — full dataset is loaded once with status=ALL. */

export function filterKycOverviewByStatus(

  payload: KycOverviewPayload,

  status: KycOverviewStatus,

): KycOverviewPayload {

  if (status === 'ALL') return payload;



  const documents = payload.documents.filter(

    (d) => String(d.document_status ?? '').trim().toUpperCase() === status,

  );

  const skills = payload.skills.filter(

    (s) => String(s.skill_status ?? '').trim().toUpperCase() === status,

  );



  const providers = payload.providers

    .map((p) => {

      const pDocs = (p.documents ?? []).filter(

        (d) => String(d.document_status ?? '').trim().toUpperCase() === status,

      );

      const pSkills = (p.skills ?? []).filter(

        (s) => String(s.skill_status ?? '').trim().toUpperCase() === status,

      );

      if (pDocs.length === 0 && pSkills.length === 0) return null;

      const pending =

        pDocs.filter((d) => isPendingStatus(d.document_status as string)).length +

        pSkills.filter((s) => isPendingStatus(s.skill_status as string)).length;

      return {

        ...p,

        documents: pDocs,

        skills: pSkills,

        total_pending: pending,

        total_items: pDocs.length + pSkills.length,

        verification_status: computeVerificationStatus(

          pDocs as { document_status?: string }[],

          pSkills as { skill_status?: string }[],

        ),

      };

    })

    .filter((p): p is KycOverviewProvider => p != null);



  return {

    ...payload,

    documents,

    skills,

    providers,

    meta: {

      ...payload.meta,

      status_filter: status,

      document_count: documents.length,

      skill_count: skills.length,

      provider_count: providers.length,

    },

  };

}



async function fetchOverviewFromApi(): Promise<KycOverviewPayload | null> {

  const res = await apiClient.get('/kyc-admin/overview', {

    params: { status: 'ALL' },

    timeout: KYC_REQUEST_TIMEOUT_MS,

  });

  const parsed = parseOverviewBody(res.data);

  if (!parsed) return null;

  return {

    ...parsed,

    meta: { ...parsed.meta, source: 'overview', status_filter: 'ALL' },

  };

}



/** Legacy fallback when /kyc-admin/overview is not deployed — parallel batch (7 calls). */

async function fetchKycOverviewLegacy(): Promise<KycOverviewPayload> {

  const statuses = ['PENDING', 'APPROVED', 'REJECTED'] as const;

  const loadWarnings: string[] = [];



  const docRequests = statuses.map((st) =>

    apiClient

      .get('/service-provider-document/fetchByStatus', {

        params: { status: st },

        timeout: KYC_REQUEST_TIMEOUT_MS,

      })

      .then((docRes) => {

        const parsed = parseKycListResponse(docRes.data);

        if (parsed.length === 0) {

          const body = docRes.data as { success?: boolean; message?: string };

          if (body?.success === false) {

            loadWarnings.push(`Documents (${st}): ${body.message ?? 'request failed'}`);

          }

        }

        return parsed;

      })

      .catch((err: unknown) => {

        const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;

        loadWarnings.push(`Documents (${st}): ${msg ?? 'network error'}`);

        return [] as Record<string, unknown>[];

      }),

  );



  const skillRequests = statuses.map((st) =>

    apiClient

      .get('/service-provider-skills/fetchByStatus', {

        params: { status: st },

        timeout: KYC_REQUEST_TIMEOUT_MS,

      })

      .then((skillRes) => {

        const parsed = parseKycListResponse(skillRes.data);

        if (parsed.length === 0) {

          const body = skillRes.data as { success?: boolean; message?: string };

          if (body?.success === false) {

            loadWarnings.push(`Skills (${st}): ${body.message ?? 'request failed'}`);

          }

        }

        return parsed;

      })

      .catch((err: unknown) => {

        const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;

        loadWarnings.push(`Skills (${st}): ${msg ?? 'network error'}`);

        return [] as Record<string, unknown>[];

      }),

  );



  const servicesRequest = apiClient

    .get('/services/fetchAllServices', { timeout: KYC_REQUEST_TIMEOUT_MS })

    .then((svcRes) => {

      const raw = (svcRes.data as { data?: unknown })?.data ?? svcRes.data;

      const list = Array.isArray(raw)

        ? raw

        : raw && typeof raw === 'object' && Array.isArray((raw as { data?: unknown }).data)

          ? (raw as { data: unknown[] }).data

          : [];

      return list.map((s: Record<string, unknown>) => ({

        id: String(s.id ?? ''),

        service_name: String(s.service_name ?? ''),

      })) as KycCatalogService[];

    })

    .catch(() => {

      loadWarnings.push('Service catalog could not be loaded');

      return [] as KycCatalogService[];

    });



  const [docChunks, skillChunks, services] = await Promise.all([

    Promise.all(docRequests),

    Promise.all(skillRequests),

    servicesRequest,

  ]);



  const seenDoc = new Set<string>();

  const documents = docChunks.flat().filter((d) => {

    const id = String(d.id ?? '');

    if (!id || seenDoc.has(id)) return false;

    seenDoc.add(id);

    return true;

  });

  const seenSkill = new Set<string>();

  const skills = skillChunks.flat().filter((s) => {

    const id = String(s.id ?? '');

    if (!id || seenSkill.has(id)) return false;

    seenSkill.add(id);

    return true;

  });



  const byProvider = new Map<string, KycOverviewProvider>();

  const ensure = (row: Record<string, unknown>) => {

    const pid = String(row.service_provider_id ?? row.user_id ?? '').trim();

    if (!pid) return null;

    if (!byProvider.has(pid)) {

      byProvider.set(pid, {

        service_provider_id: pid,

        user_id: String(row.user_id ?? pid),

        service_provider_name: (row.service_provider_name as string) ?? null,

        service_provider_email: (row.service_provider_email as string) ?? null,

        service_provider_phone: (row.service_provider_phone as string) ?? null,

        provider_role: (row.provider_role as string) ?? null,

        verification_status: 'PENDING',

        total_pending: 0,

        total_items: 0,

        documents: [],

        skills: [],

      });

    }

    return byProvider.get(pid)!;

  };



  documents.forEach((d) => {

    const g = ensure(d);

    if (g) g.documents.push(d);

  });

  skills.forEach((s) => {

    const g = ensure(s);

    if (g) g.skills.push(s);

  });



  const providers = Array.from(byProvider.values()).map((p) => {

    const pending =

      p.documents.filter((d) => isPendingStatus(d.document_status as string)).length +

      p.skills.filter((s) => isPendingStatus(s.skill_status as string)).length;

    const sample = (p.documents[0] ?? p.skills[0] ?? {}) as Record<string, unknown>;

    return {

      ...p,

      service_provider_name:

        p.service_provider_name ?? (sample.service_provider_name as string) ?? null,

      service_provider_email:

        p.service_provider_email ?? (sample.service_provider_email as string) ?? null,

      service_provider_phone:

        p.service_provider_phone ?? (sample.service_provider_phone as string) ?? null,

      provider_role: p.provider_role ?? (sample.provider_role as string) ?? null,

      verification_status: computeVerificationStatus(

        p.documents as { document_status?: string }[],

        p.skills as { skill_status?: string }[],

      ),

      total_pending: pending,

      total_items: p.documents.length + p.skills.length,

    };

  });



  if (documents.length === 0 && skills.length === 0 && loadWarnings.length > 0) {

    console.warn('[KYC] Legacy load returned no rows:', loadWarnings);

  }



  return {

    documents,

    skills,

    providers,

    services,

    meta: {

      status_filter: 'ALL',

      document_count: documents.length,

      skill_count: skills.length,

      provider_count: providers.length,

      source: 'legacy',

      load_warnings: loadWarnings.length > 0 ? loadWarnings : undefined,

    },

  };

}



async function loadFullKycOverview(force = false): Promise<KycOverviewPayload> {

  if (!force && overviewCache && Date.now() - overviewCache.at < OVERVIEW_CACHE_MS) {

    return overviewCache.payload;

  }



  if (!force && overviewInflight) {

    return overviewInflight;

  }



  const task = (async () => {

    try {

      const fromApi = await fetchOverviewFromApi();

      if (fromApi) return fromApi;

      console.warn('[KYC] GET /kyc-admin/overview unavailable — using legacy batch (deploy latest ots_be for 1-call load)');

    } catch (err: unknown) {

      const statusCode = (err as { response?: { status?: number } })?.response?.status;

      if (statusCode !== 404 && statusCode !== 501) {

        throw err;

      }

      console.warn('[KYC] GET /kyc-admin/overview returned', statusCode, '— using legacy batch');

    }



    return fetchKycOverviewLegacy();

  })();



  overviewInflight = task;

  try {

    const payload = await task;

    overviewCache = { at: Date.now(), payload };

    return payload;

  } finally {

    overviewInflight = null;

  }

}



/** Invalidate cached overview after admin approve/reject. */

export function invalidateKycOverviewCache(): void {

  overviewCache = null;

}



/**

 * Load admin KYC data — one GET /kyc-admin/overview when backend is deployed.

 * Status filter is applied client-side from the cached full payload.

 */

export async function fetchKycOverview(

  status: KycOverviewStatus = 'ALL',

  options?: { force?: boolean },

): Promise<KycOverviewPayload> {

  const full = await loadFullKycOverview(options?.force === true);

  return filterKycOverviewByStatus(full, status);

}



export function providerLabel(

  pid: string,

  row: {

    service_provider_name?: string | null;

    service_provider_email?: string | null;

    service_provider_phone?: string | null;

  },

): string {

  return providerLabelFromRow(pid, row as Record<string, unknown>);

}


